import React, { useCallback, useContext } from 'react';
import { Segmented } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { doc, updateDoc } from 'firebase/firestore';

import { AuthContext } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useAppTranslation } from '../hooks/useAppTranslation';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { isAppLocale } from '../i18n/types';

type PublicLanguageSwitcherProps = {
  size?: 'small' | 'middle';
};

/**
 * Sélecteur FR | EN pour pages pré-auth (landing, login) et sélection business.
 * - Toujours visible
 * - Changement UI immédiat (localStorage)
 * - Persist Firestore en arrière-plan si connecté (échec ≠ rollback UI)
 */
export const PublicLanguageSwitcher: React.FC<PublicLanguageSwitcherProps> = ({
  size = 'small',
}) => {
  const { locale, setLocale } = useLocale();
  const { t } = useAppTranslation('common');
  const auth = useContext(AuthContext);

  const onChange = useCallback(
    (raw: string | number) => {
      const next = String(raw);
      if (!isAppLocale(next) || next === locale) return;

      setLocale(next);

      if (!auth?.user) return;

      auth.patchUserProfile({ preferredLocale: next });
      void updateDoc(doc(db, COLLECTIONS.users, auth.user.uid), {
        preferredLocale: next,
      }).catch((error) => {
        console.warn('Impossible de sauvegarder preferredLocale (UI conservée):', error);
      });
    },
    [locale, setLocale, auth]
  );

  return (
    <Segmented
      size={size}
      value={locale}
      onChange={onChange}
      options={[
        { label: t('language.fr'), value: 'fr', icon: <GlobalOutlined /> },
        { label: t('language.en'), value: 'en' },
      ]}
    />
  );
};
